import type { ApiRoomChatAccessJSON } from 'mastodon/api_types/rooms';

/**
 * Ticket 72 - the viewer's side of a host's followers-only chat.
 *
 * The rule itself is the server's (ChatAccessPolicy), and the server is the
 * only thing that actually refuses a message. Everything here exists so the
 * composer can say what the rule is BEFORE someone types a paragraph into a
 * box that was never going to accept it.
 *
 * Two states matter and they are not the same:
 *
 *   locked   - you do not follow the host. There is no clock; following is the
 *              only thing that changes it.
 *   counting - you follow, but not for long enough yet. There IS a clock, and
 *              showing it is the entire point of the ticket.
 */

export type ChatAccessGate =
  | 'followers_only'
  | 'follow_age'
  | 'account_age'
  | 'unknown';

export interface ChatAccessState {
  /** Whether the composer should accept input. */
  canChat: boolean;
  gate: ChatAccessGate | null;
  /** Server's sentence, when there is one worth showing verbatim. */
  message: string | null;
  /** Whole seconds remaining, 0 when there is no clock. */
  secondsLeft: number;
  /** True only for a gate that waiting alone will clear. */
  counting: boolean;
}

export const OPEN: ChatAccessState = {
  canChat: true,
  gate: null,
  message: null,
  secondsLeft: 0,
  counting: false,
};

/**
 * A countdown must never be longer than the rule it is counting. A client
 * whose clock is behind the server's would otherwise turn a 10-second wait
 * into a visible several-minute one; worse, a clock far enough behind would
 * show a countdown that never appears to move. Clamping to the rule's own
 * length means the worst a skewed clock can do is unlock a moment early, and
 * the server is still the thing that decides.
 */
const clampSeconds = (seconds: number, requiredSeconds: number): number => {
  if (!Number.isFinite(seconds) || seconds <= 0) return 0;
  const ceiling =
    Number.isFinite(requiredSeconds) && requiredSeconds > 0
      ? requiredSeconds
      : seconds;
  return Math.min(Math.ceil(seconds), Math.ceil(ceiling));
};

const normaliseGate = (gate?: string | null): ChatAccessGate | null => {
  if (!gate) return null;
  if (
    gate === 'followers_only' ||
    gate === 'follow_age' ||
    gate === 'account_age'
  )
    return gate;
  // A gate this build has never heard of still blocks - the server refused,
  // and pretending otherwise would put the composer back to "type and find
  // out". It just cannot claim to know whether waiting helps.
  return 'unknown';
};

/**
 * Fold the server's verdict, the wall clock, and any locally-started countdown
 * into what the composer should do.
 *
 * `localUnlockAt` is set the instant the viewer presses Follow, so the
 * countdown starts on the click rather than on the next 3s room poll. It only
 * ever RELAXES the verdict once it has expired: while it is still running the
 * viewer is counting either way, and a local timer may never unlock someone
 * the server is still refusing for a different reason.
 */
export const resolveChatAccess = (
  access: ApiRoomChatAccessJSON | null | undefined,
  nowMs: number,
  localUnlockAt?: number | null,
): ChatAccessState => {
  const localCounting =
    typeof localUnlockAt === 'number' && localUnlockAt > nowMs;

  // No field at all: an older server, or a viewer the server did not compute
  // one for. Never gate on an absence.
  if (!access) {
    if (!localCounting) return OPEN;
    return {
      canChat: false,
      gate: 'follow_age',
      message: null,
      secondsLeft: Math.ceil((localUnlockAt - nowMs) / 1000),
      counting: true,
    };
  }

  if (access.can_chat && !localCounting) return OPEN;

  const gate = normaliseGate(access.gate) ?? 'follow_age';
  const required = access.follow_seconds_required ?? 0;

  let secondsLeft = 0;

  if (access.unlocks_at) {
    const unlockMs = Date.parse(access.unlocks_at);
    if (!Number.isNaN(unlockMs))
      secondsLeft = clampSeconds((unlockMs - nowMs) / 1000, required);
  }

  if (localCounting) {
    const localLeft = clampSeconds((localUnlockAt - nowMs) / 1000, required);
    secondsLeft = Math.max(secondsLeft, localLeft);
  }

  // The server said no and the clock it gave us has run out: the next poll
  // will confirm. Staying blocked in the meantime is the honest state - the
  // send would still be refused.
  const counting = secondsLeft > 0;

  if (access.can_chat && !counting) return OPEN;

  return {
    canChat: false,
    gate,
    message: access.message ?? null,
    secondsLeft,
    counting,
  };
};

/**
 * When to unlock after pressing Follow, or null when following does not start
 * a clock (no minimum follow time set - chat opens at once).
 */
export const followUnlockAt = (
  access: ApiRoomChatAccessJSON | null | undefined,
  nowMs: number,
): number | null => {
  const required = access?.follow_seconds_required ?? 0;
  if (!Number.isFinite(required) || required <= 0) return null;
  return nowMs + required * 1000;
};

/** What the composer says in place of "Send a message...". */
export const chatAccessPlaceholder = (state: ChatAccessState): string => {
  if (state.canChat) return 'Send a message...';
  if (state.counting)
    return `Chat unlocks in ${state.secondsLeft}${state.secondsLeft === 1 ? ' second' : ' seconds'}...`;
  if (state.gate === 'followers_only') return 'Follow the host to chat';
  return state.message ?? 'You cannot post in this chat yet';
};
